import { useTranslation } from 'react-i18next';
import { memo, useCallback, useMemo, useState } from 'react';
import { Stack, Divider } from '@mui/material';

import { Accordion } from '@/modules/common/components/Accordion';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { SimulationServiceFactory } from '../../service/SimulationServiceFactory';
import { FmsConfig } from '../../helpers/customTypes';
import { FailOnNoRouteFoundToggle } from './FailOnNoRouteFoundToggle';
import { DownloadAndUploadFMSConfig } from './DownloadAndUploadFMSConfig';
import { UploadOrderProfile } from './UploadOrderProfile';
import { ManualInterventionToggle } from './ManualInterventionToggle';
import { ObstructionTimeInSeconds } from './ObstructionTimeInSeconds';
import DistributionStrategy from './DistributionStrategy';
import UvtOverrides from './UvtOverrides';

interface Props {
  disabled?: boolean;
  customOrderProfileUrl?: string;
  onUploadOrderProfile: (url: string) => void;
  onRemoveUploadedOrderProfile: () => void;
  customFmsConfigUrl?: string;
  onUploadFmsConfig: (url: string, fmsConfig: FmsConfig) => void;
  onRemoveUploadedFmsConfig: () => void;
}

function AdvancedSettings({
  disabled = false,
  customOrderProfileUrl,
  onUploadOrderProfile,
  onRemoveUploadedOrderProfile,
  customFmsConfigUrl,
  onUploadFmsConfig,
  onRemoveUploadedFmsConfig,
}: Props) {
  const { t } = useTranslation();
  const simSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const [open, setOpen] = useState(false);

  //
  // Component values
  //

  const {
    failOnNoRouteFound,
    distributionStrategy,
    manualIntervention,
    obstructionTimeOutInSeconds,
  } = simSvc.useDraft().config.fmsConfig;

  //
  // Component updates
  //

  const onFailOnNoRouteFoundChanged = useCallback(
    (failOnNoRouteFound: boolean): void => {
      simSvc.updateDraft({
        config: {
          fmsConfig: {
            failOnNoRouteFound,
          },
        },
      });
      fddmSvc.setPreference('simulationConfig.fmsConfig.failOnNoRouteFound', failOnNoRouteFound);
    },
    [fddmSvc, simSvc],
  );

  const onManualInterventionChanged = useCallback(
    (manualIntervention: boolean): void => {
      simSvc.updateDraft({ config: { fmsConfig: { manualIntervention } } });
      fddmSvc.setPreference('simulationConfig.fmsConfig.manualIntervention', manualIntervention);
    },
    [fddmSvc, simSvc],
  );

  const onObstructionTimeOutChanged = useCallback(
    (obstructionTimeOut: number): void => {
      simSvc.updateDraft({
        config: { fmsConfig: { obstructionTimeOutInSeconds: obstructionTimeOut } },
      });
      fddmSvc.setPreference(
        'simulationConfig.fmsConfig.obstructionTimeoutInSeconds',
        obstructionTimeOut,
      );
    },
    [fddmSvc, simSvc],
  );

  const onDistributionStrategyChanged = useCallback(
    (distributionStrategy: number): void => {
      simSvc.updateDraft({ config: { fmsConfig: { distributionStrategy } } });
      fddmSvc.setPreference(
        'simulationConfig.fmsConfig.distributionStrategy',
        distributionStrategy,
      );
    },
    [fddmSvc, simSvc],
  );

  const handleUploadOrderProfile = (filePath: string) => {
    onUploadOrderProfile(filePath);
  };

  const handleUploadFmsConfig = (url: string, fmsConfig: FmsConfig) => {
    onUploadFmsConfig(url, fmsConfig);
  };

  return (
    <Accordion
      title={t('interface:simulation.experimental_settings.header', 'Experimental settings')}
      expanded={open}
      onChange={(expanded: boolean) => setOpen(expanded)}
      sx={{ px: 2 }}
    >
      <Stack height='100%'>
        <Stack
          sx={{
            flex: '1 1 auto',
            overflowY: 'scroll',
            overflow: 'visible',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
          gap={1}
        >
          <FailOnNoRouteFoundToggle
            value={failOnNoRouteFound}
            onChange={onFailOnNoRouteFoundChanged}
          />
          <ManualInterventionToggle
            value={manualIntervention}
            onChange={onManualInterventionChanged}
          />
          <ObstructionTimeInSeconds
            value={obstructionTimeOutInSeconds}
            onChange={onObstructionTimeOutChanged}
          />
          <DistributionStrategy
            value={distributionStrategy}
            onChange={onDistributionStrategyChanged}
          />
          <Divider />
          <UvtOverrides />
          <Divider />
          <UploadOrderProfile
            customOrderProfileUrl={customOrderProfileUrl}
            onUploadOrderProfile={handleUploadOrderProfile}
            onRemoveUploadedOrderProfile={onRemoveUploadedOrderProfile}
          />
          <Divider />
          <DownloadAndUploadFMSConfig
            disabled={disabled}
            customFmsConfigUrl={customFmsConfigUrl}
            onUploadFmsConfig={handleUploadFmsConfig}
            onRemoveUploadedFmsConfig={onRemoveUploadedFmsConfig}
          />
        </Stack>
      </Stack>
    </Accordion>
  );
};

export default memo(AdvancedSettings)